import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spin, Pagination, notification } from "antd";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../store/config";
import { SortType } from "../../../interfaces/screens/fetch-params-types";
import { CARDS_PAGE_SIZE, IMAGE_BASE_URL } from "../../../common/config";
import { useDebounce } from "../../../hooks/useDebounce";
import DeleteModal from "../../../components/common/DeleteModal";
import { deleteRecipeStart } from "../../../store/features/recipe/recipe-slice";
import { fetchRecipesStart } from "../../../store/features/recipes/recipes-slice";
import {
  StyledCardTableLayout,
  StyledCardImage,
  StyledCard,
  CardActionsButton,
  StyledCardMetaWrapper,
  StyledCardMeta,
} from "../../../components/common/styles/tableStyles";
import { TableHeader } from "../../../components/table/TableHeader";
import { HeaderHeadline, StyledFlex } from "./style";
import { ReactComponent as DotsActionsIcon } from "../../../assets/icons/ThreeDotsVertical.svg";
import { NotFoundText } from "../../../components/common/styles";
import { IFetchParams } from "../../../interfaces/base";

export const RecipesTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [api, contextHolder] = notification.useNotification();

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [currentRecipeId, setCurrentRecipeId] = useState<string>("");
  const [deleteConfirmLoading, setDeleteConfirmLoading] =
    useState<boolean>(false);

  const { recipes, loading, error, metadata, tableState } = useAppSelector(
    (state) => state.recipes
  );
  const { search, sort, pagination } = tableState;
  const [inputSearch, setInputSearch] = useState<string>(search);

  const debouncedSearch = useDebounce(inputSearch, 300);

  const {
    error: deleteError,
    operation: deleteOperation,
    requestState: deleteRequestState,
  } = useAppSelector((state) => state.recipe);

  useEffect(() => {
    const paginationToUse =
      debouncedSearch !== search
        ? { page: 0, limit: CARDS_PAGE_SIZE, skip: 0 }
        : pagination;

    fetchRecipes({ pagination: paginationToUse });
  }, [debouncedSearch]);

  useEffect(() => {
    if (error || deleteError) {
      openNotification();
    }
  }, [error, deleteError]);

  useEffect(() => {
    if (!openDeleteModal) {
      setCurrentRecipeId("");
    }
  }, [openDeleteModal]);

  useEffect(() => {
    if (deleteRequestState === "finish" && deleteOperation === "delete") {
      setDeleteConfirmLoading(false);
      setOpenDeleteModal(false);
      fetchRecipes({});
    }
  }, [deleteRequestState, deleteOperation]);

  const openNotification = () => {
    api["error"]({
      message: t("Something went wrong, please try again"),
      placement: "bottomLeft",
    });
  };

  const onPaginationChange = (page: number) => {
    const newPagination = {
      ...pagination,
      page,
      skip: CARDS_PAGE_SIZE * (page - 1),
    };

    fetchRecipes({ pagination: newPagination });
  };

  const handleSetSort = (value: SortType) => {
    fetchRecipes({
      sort: value,
      pagination: { page: 0, limit: CARDS_PAGE_SIZE, skip: 0 },
    });
  };

  const handleDelete = () => {
    setDeleteConfirmLoading(true);
    dispatch(deleteRecipeStart({ recipeId: currentRecipeId }));
  };

  const handleCreateNewRecipe = () => {
    navigate(`/diet/add`);
  };

  const fetchRecipes = (body: IFetchParams) => {
    dispatch(
      fetchRecipesStart({
        search: body.search || debouncedSearch,
        sort: body.sort || sort,
        pagination: body.pagination || pagination,
      })
    );
  };

  return (
    <>
      {contextHolder}
      <StyledFlex>
        <HeaderHeadline>{t("Diet")}</HeaderHeadline>
        <TableHeader
          search={inputSearch}
          setSearch={setInputSearch}
          setSort={handleSetSort}
          handleCreateNew={handleCreateNewRecipe}
          createNewText="Create New Recipe"
          currentSortBy={sort?.sortBy || ""}
        />
      </StyledFlex>
      <Spin spinning={loading}>
        {recipes.length === 0 && !loading && (
          <NotFoundText>{t("No Recipes found")}</NotFoundText>
        )}
        <StyledCardTableLayout>
          {recipes.map((recipe) => (
            <StyledCard
              key={recipe._id}
              cover={
                <StyledCardImage
                  src={IMAGE_BASE_URL + "/" + recipe.innerCover}
                />
              }
            >
              <CardActionsButton
                menu={{
                  items: [
                    {
                      key: "edit",
                      label: t("Edit"),
                      onClick: () =>
                        navigate(`/diet/${recipe._id}`, {
                          state: { isEdit: true },
                        }),
                    },
                    {
                      key: "delete",
                      label: t("Delete"),
                      onClick: () => {
                        setCurrentRecipeId(recipe._id);
                        setOpenDeleteModal(true);
                      },
                    },
                  ],
                }}
              >
                <DotsActionsIcon />
              </CardActionsButton>
              <StyledCardMetaWrapper
                onClick={() => navigate(`/diet/${recipe._id}`)}
              >
                <StyledCardMeta
                  title={recipe.name}
                  description={recipe.description || ""}
                />
              </StyledCardMetaWrapper>
            </StyledCard>
          ))}
        </StyledCardTableLayout>
        <Pagination
          current={metadata?.page}
          pageSize={CARDS_PAGE_SIZE}
          total={metadata?.total}
          align="center"
          hideOnSinglePage
          onChange={onPaginationChange}
          showSizeChanger={false}
          style={{ direction: "ltr", marginTop: "60px" }}
        />
        <DeleteModal
          open={openDeleteModal}
          setOpen={setOpenDeleteModal}
          handleOk={handleDelete}
          confirmLoading={deleteConfirmLoading}
        />
      </Spin>
    </>
  );
};
